import React from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";

type TargetUser = {
  _id: string;
  profilename?: string | null;
  picture?: string | null;
};

type Props = {
  target: TargetUser | null;
  onBlocked?: (userId: string) => void;
  onRequestReport?: (userId: string) => void;
  className?: string;
  /** Size of the 3-dots button (px). Defaults to 44. */
  size?: number;
};

const menuBox: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  right: 0,
  minWidth: 220,
  background: "#fff",
  border: "0.1px solid rgba(0,0,0,0.1)",
  borderRadius: 10,
  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  padding: 8,
  zIndex: 50,
};

const itemBtn: React.CSSProperties = {
  width: "100%",
  background: "transparent",
  border: 0,
  textAlign: "left",
  padding: "10px 12px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 14,
};

const itemBtnDisabled: React.CSSProperties = {
  ...itemBtn,
  opacity: 0.5,
  cursor: "not-allowed",
};

const KebabIcon: React.FC<{ size: number; color?: string }> = ({
  size,
  color = "#111",
}) => {
  const icon = Math.round(size * 0.55); // scale icon within button
  return (
    <svg
      width={icon}
      height={icon}
      viewBox="0 0 24 24"
      fill={color}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
};

export default function ProfileActions({
  target,
  onBlocked,
  onRequestReport,
  className,
  size = 44,
}: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = React.useState(true);
  const [isFollowing, setIsFollowing] = React.useState<boolean>(false);
  const [iBlock, setIBlock] = React.useState<boolean>(false);
  const [theyBlock, setTheyBlock] = React.useState<boolean>(false);

  const myId = user?._id || "";
  const targetId = target?._id || "";
  const isSelf = myId && targetId && myId === targetId;

  // Close on outside click / Esc
  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (btnRef.current && btnRef.current.contains(t)) return;
      if (menuRef.current && menuRef.current.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Fetch follow + block state
  React.useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!targetId || !myId || isSelf) {
        setLoading(false);
        setIsFollowing(false);
        setIBlock(false);
        setTheyBlock(false);
        return;
      }
      try {
        setLoading(true);
        const [stats, block] = await Promise.all([
          api.get(`/follows/${encodeURIComponent(targetId)}/stats`).catch(() => ({ data: { isFollowing: false } })),
          api.get(`/block-report/block/check/${encodeURIComponent(targetId)}`).catch(() => ({ data: { iBlock: false, theyBlock: false } })),
        ]);
        if (cancelled) return;
        setIsFollowing(!!stats?.data?.isFollowing);
        setIBlock(!!block?.data?.iBlock);
        setTheyBlock(!!block?.data?.theyBlock);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [targetId, myId, isSelf]);

  const goToPosts = () => {
    if (!targetId) return;
    navigate(`/visitor-profile/${encodeURIComponent(targetId)}`);
    setOpen(false);
  };

  const toggleFollow = async () => {
    if (!targetId || isSelf) return;
    try {
      if (isFollowing) {
        await api.delete(`/follows/${encodeURIComponent(targetId)}`);
        setIsFollowing(false);
      } else {
        await api.post(`/follows/${encodeURIComponent(targetId)}`);
        setIsFollowing(true);
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.msg ||
        e?.message ||
        t("profileActions.errors.actionFailed");
      alert(msg);
    } finally {
      setOpen(true);
    }
  };


  const block = async () => {
    if (!targetId || isSelf) return;
    const name = target?.profilename
      ? `@${target.profilename}`
      : t("profileActions.thisUser");

    const ok = window.confirm(
      t("profileActions.blockConfirm", { name })
    );
    if (!ok) return;

    try {
      await api.post(`/block-report/block/${encodeURIComponent(targetId)}`);
      setIBlock(true);
      setOpen(false);
      onBlocked?.(targetId);
      alert(t("profileActions.blocked"));
    } catch (e: any) {
      const msg =
        e?.response?.data?.msg ||
        e?.message ||
        t("profileActions.errors.blockFailed");
      alert(msg);
    }
  };

  const unblock = async () => {
    if (!targetId || isSelf) return;
    try {
      await api.post(`/block-report/unblock/${encodeURIComponent(targetId)}`);
      setIBlock(false);
      setOpen(false);
      alert(t("profileActions.unblocked"));
    } catch (e: any) {
      const msg =
        e?.response?.data?.msg ||
        e?.message ||
        t("profileActions.errors.unblockFailed");
      alert(msg);
    }
  };

  const blockAndReport = async () => {
    if (!targetId || isSelf) return;
    try {
      if (!iBlock) {
        await api.post(`/block-report/block/${encodeURIComponent(targetId)}`);
      }
      setIBlock(true);
      setOpen(false);
      onBlocked?.(targetId);
      if (onRequestReport) {
        onRequestReport(targetId);
      } else {
        alert(t("profileActions.blockedWithInfo"));
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.msg ||
        e?.message ||
        t("profileActions.errors.blockFailed");
      alert(msg);
    }
  };

  const followDisabled = isSelf || iBlock || theyBlock || loading;

  if (!targetId || isSelf) return null;

  return (
    <div className={className} style={{ position: "relative" }}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        title={t("profileActions.moreActions")}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        style={{
          width: size,
          height: size,
          borderRadius: 8,
          border: "1px solid rgba(0,0,0,0.08)",
          background: "transparent",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <KebabIcon size={size} />
      </button>

      {open && (
        <div ref={menuRef} role="menu" style={menuBox}>
          {/* 1) Posts */}
          <button
            type="button"
            role="menuitem"
            style={itemBtn}
            onClick={goToPosts}
          >
            {target?.profilename
              ? t("profileActions.postsNamed", {
                  name: target.profilename,
                })
              : t("profileActions.postsGeneric")}
          </button>

          {/* 2) Follow / Unfollow */}
          <button
            type="button"
            role="menuitem"
            style={followDisabled ? itemBtnDisabled : itemBtn}
            disabled={followDisabled}
            onClick={toggleFollow}
          >
            {isFollowing
              ? t("profileActions.unfollow")
              : t("profileActions.follow")}
          </button>

          {/* 3) Block / Unblock */}
          {iBlock ? (
            <button
              type="button"
              role="menuitem"
              style={itemBtn}
              onClick={unblock}
            >
              {t("profileActions.unblock")}
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              style={itemBtn}
              onClick={block}
            >
              {t("profileActions.block")}
            </button>
          )}

          {/* 4) Block & Report */}
          {!iBlock && (
            <button
              type="button"
              role="menuitem"
              style={itemBtn}
              onClick={blockAndReport}
            >
              {t("profileActions.blockAndReport")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
